import { onBeforeUnmount } from 'vue';
import { useEventHub } from './useEventHub';

/**
 * Composable for ActionEvents - modern replacement for ActionEvents mixins
 * Provides type-safe event handling with automatic cleanup
 */
export function useActionEvents(scope: string): ReturnType<typeof createUseActionEvents>;
export function useActionEvents(options?: {
	scope?: string;
	autoRegister?: boolean;
}): ReturnType<typeof createUseActionEvents>;
export function useActionEvents(
	scopeOrOptions?:
		| string
		| {
				scope?: string;
				autoRegister?: boolean;
		  },
) {
	// Handle overloads
	let scope = '';
	let autoRegister = true;

	if (typeof scopeOrOptions === 'string') {
		scope = scopeOrOptions;
	} else if (scopeOrOptions) {
		scope = scopeOrOptions.scope || '';
		autoRegister = scopeOrOptions.autoRegister ?? true;
	}

	return createUseActionEvents(scope, autoRegister);
}

function createUseActionEvents(scope: string, autoRegister: boolean) {
	const eventHub = useEventHub();

	// Store registered handlers for cleanup
	type HandlerFunction = (data?: any) => void;
	const registeredHandlers = new Map<string, HandlerFunction>();

	/**
	 * Create scoped event name
	 */
	const createEventName = (event: string, useScope = false) => {
		return (useScope ? scope : '') + event;
	};

	/**
	 * Register save event handler
	 */
	const onSave = (handler: (data?: any) => void, event = '_save', useScope?: boolean) => {
		// Auto-detect useScope: use true if scope is defined and useScope is not explicitly set to false
		const shouldUseScope = useScope !== undefined ? useScope : !!scope;
		const eventName = createEventName(event, shouldUseScope);
		registeredHandlers.set(`save:${eventName}`, handler);

		if (autoRegister) {
			eventHub.on(eventName, handler);
		}

		return () => eventHub.off(eventName, handler);
	};

	/**
	 * Register refresh event handler
	 */
	const onRefresh = (handler: (data?: any) => void, event = '_refresh', useScope?: boolean) => {
		// Auto-detect useScope: use true if scope is defined and useScope is not explicitly set to false
		const shouldUseScope = useScope !== undefined ? useScope : !!scope;
		const eventName = createEventName(event, shouldUseScope);
		registeredHandlers.set(`refresh:${eventName}`, handler);

		if (autoRegister) {
			eventHub.on(eventName, handler);
		}

		return () => eventHub.off(eventName, handler);
	};

	/**
	 * Register language change event handler
	 */
	const onLanguageChange = (
		handler: (data?: any) => void,
		event = '_languageChange',
		useScope?: boolean,
	) => {
		const shouldUseScope = useScope !== undefined ? useScope : !!scope;
		const eventName = createEventName(event, shouldUseScope);
		registeredHandlers.set(`languageChange:${eventName}`, handler);

		if (autoRegister) {
			eventHub.on(eventName, handler);
		}

		return () => eventHub.off(eventName, handler);
	};

	/**
	 * Register add pending save event handler
	 */
	const onAddPendingSave = (
		handler: (data?: any) => void,
		event = '_addPendingSave',
		useScope?: boolean,
	) => {
		const shouldUseScope = useScope !== undefined ? useScope : !!scope;
		const eventName = createEventName(event, shouldUseScope);
		registeredHandlers.set(`addPendingSave:${eventName}`, handler);

		if (autoRegister) {
			eventHub.on(eventName, handler);
		}

		return () => eventHub.off(eventName, handler);
	};

	/**
	 * Register clear pending save event handler
	 */
	const onClearPendingSave = (
		handler: (data?: any) => void,
		event = '_clearPendingSave',
		useScope?: boolean,
	) => {
		const shouldUseScope = useScope !== undefined ? useScope : !!scope;
		const eventName = createEventName(event, shouldUseScope);
		registeredHandlers.set(`clearPendingSave:${eventName}`, handler);

		if (autoRegister) {
			eventHub.on(eventName, handler);
		}

		return () => eventHub.off(eventName, handler);
	};

	/**
	 * Register refresh title event handler
	 */
	const onRefreshTitle = (
		handler: (data?: any) => void,
		event = '_refreshTitle',
		useScope?: boolean,
	) => {
		const shouldUseScope = useScope !== undefined ? useScope : !!scope;
		const eventName = createEventName(event, shouldUseScope);
		registeredHandlers.set(`refreshTitle:${eventName}`, handler);

		if (autoRegister) {
			eventHub.on(eventName, handler);
		}

		return () => eventHub.off(eventName, handler);
	};

	/**
	 * Emit save event
	 */
	const emitSave = (data?: any, event = '_save', useScope?: boolean) => {
		const shouldUseScope = useScope !== undefined ? useScope : !!scope;
		const eventName = createEventName(event, shouldUseScope);
		eventHub.emit(eventName, data);
	};

	/**
	 * Emit refresh event
	 */
	const emitRefresh = (data?: any, event = '_refresh', useScope?: boolean) => {
		const shouldUseScope = useScope !== undefined ? useScope : !!scope;
		const eventName = createEventName(event, shouldUseScope);
		eventHub.emit(eventName, data);
	};

	/**
	 * Emit language change event
	 */
	const emitLanguageChange = (data?: any, event = '_languageChange', useScope = false) => {
		const eventName = createEventName(event, useScope);
		eventHub.emit(eventName, data);
	};

	/**
	 * Emit add pending save event
	 */
	const emitAddPendingSave = (data?: any, event = '_addPendingSave', useScope = false) => {
		const eventName = createEventName(event, useScope);
		eventHub.emit(eventName, data);
	};

	/**
	 * Emit clear pending save event
	 */
	const emitClearPendingSave = (data?: any, event = '_clearPendingSave', useScope = false) => {
		const eventName = createEventName(event, useScope);
		eventHub.emit(eventName, data);
	};

	/**
	 * Emit refresh title event
	 */
	const emitRefreshTitle = (data?: any, event = '_refreshTitle', useScope = false) => {
		const eventName = createEventName(event, useScope);
		eventHub.emit(eventName, data);
	};

	// Cleanup is handled automatically by useEventHub
	onBeforeUnmount(() => {
		registeredHandlers.clear();
	});

	return {
		// Event handlers
		onSave,
		onRefresh,
		onLanguageChange,
		onAddPendingSave,
		onClearPendingSave,
		onRefreshTitle,

		// Event emitters
		emitSave,
		emitRefresh,
		emitLanguageChange,
		emitAddPendingSave,
		emitClearPendingSave,
		emitRefreshTitle,

		// Utilities
		createEventName,
		scope,
	};
}
